import { JSONSchema7 } from "json-schema"
import { JsonFilter, JsonFilterMatchType } from "../API/JobModel"
import { jsonSchemaV004 } from "universal-schema/lib/0.0.4/schema_json"
import upperFirst from "lodash/upperFirst"
import lowerCase from "lodash/lowerCase"
import { UniversalSchemaType } from "universal-schema/lib/0.0.4"
import {
    isJSONSchema7,
    VirtuallyStructured,
    SchemaForm,
    findUniversalSchemaTypeByField
} from "universal-schema/lib"

export type FilterableJsonFieldType = "string" | "boolean" | "number" | "object"

export interface FilterableJsonField {
    path: (string | number)[]
    label: string
    type: FilterableJsonFieldType
}

export function hasMatchedPath(
    field: FilterableJsonField,
    filter: JsonFilter
): boolean {
    return (
        filter.path.length === field.path.length &&
        filter.path.every((p, i) => p === field.path[i])
    )
}

export function getApplicableMatchTypes(
    fieldType: FilterableJsonFieldType
): JsonFilterMatchType[] {
    switch (fieldType) {
        case "object":
            return ["CONTAINS", "CONTAINS_NOT", "IS_NOT_EMPTY"]
        case "boolean":
        case "number":
            return ["IS", "IS_NOT", "IS_NOT_EMPTY"]
        case "string":
            return ["IS", "IS_NOT", "CONTAINS", "CONTAINS_NOT", "IS_NOT_EMPTY"]
    }
}

export function castToJsonFieldType(
    value: unknown,
    fieldType: FilterableJsonFieldType
): string | boolean | number {
    return fieldType === "string" || fieldType === "object"
        ? String(value)
        : fieldType === "boolean"
        ? Boolean(value)
        : parseFloat(String(value))
}

export function collectFilterableFields(
    rootSchema: SchemaForm,
    rootData: unknown
): FilterableJsonField[] {
    const definitions = Object.values<JSONSchema7>(
        jsonSchemaV004.definitions as any
    )

    type FilterableJsonFieldTmp = Omit<FilterableJsonField, "label"> & {
        uiPath?: (string | number)[]
    }

    function collectStructured(
        structured: VirtuallyStructured,
        basePath: (string | number)[]
    ): FilterableJsonFieldTmp[] {
        return structured
            .map<FilterableJsonFieldTmp | undefined>(
                ({ field_type, field_id }, index) => {
                    const path = basePath.concat([index, "field_value"])
                    const uiPath = basePath.concat([field_id])

                    switch (field_type) {
                        case UniversalSchemaType.TEXT:
                            return { type: "string", path, uiPath }
                        case UniversalSchemaType.INTEGER:
                        case UniversalSchemaType.NUMBER:
                            return { type: "number", path, uiPath }
                        case UniversalSchemaType.BINARY_CHOICE:
                            return { type: "boolean", path, uiPath }
                        case UniversalSchemaType.EXCLUSIVE_CHOICE:
                            return {
                                type: "string",
                                path: path.concat(["selection", "value"]),
                                uiPath
                            }
                    }

                    return undefined
                }
            )
            .filter(
                (value): value is FilterableJsonFieldTmp => value !== undefined
            )
    }

    function collectImpl(
        schema: SchemaForm,
        data: unknown
    ): FilterableJsonFieldTmp[] {
        const uiField = schema.uiSchema["ui:field"]
        const type =
            schema.schema.$ref !== undefined &&
            !schema.schema.$ref.startsWith("#")
                ? schema.schema.$ref
                : typeof uiField === "string"
                ? findUniversalSchemaTypeByField(uiField)
                : undefined

        if (type === UniversalSchemaType.EXCLUSIVE_CHOICE) {
            return [{ type: "string", path: ["selection", "value"] }]
        }

        if (type !== undefined) {
            const resolved = definitions.find(d => d.$id === type)

            if (resolved === undefined) {
                return []
            }

            return collectImpl({ schema: resolved, uiSchema: {} }, data)
        }

        if (
            schema.schema.type === "boolean" ||
            schema.schema.type === "number" ||
            schema.schema.type === "string"
        ) {
            return [{ path: [], type: schema.schema.type }]
        }

        if (schema.schema.type === "integer") {
            return [{ path: [], type: "number" }]
        }

        if (schema.schema.type === "object") {
            return Object.entries(schema.schema.properties ?? {}).flatMap(
                ([field, childSchema]) =>
                    isJSONSchema7(childSchema)
                        ? collectImpl(
                              {
                                  schema: childSchema,
                                  uiSchema: schema.uiSchema[field] ?? {}
                              },
                              typeof data === "object" && data !== null
                                  ? (data as any)[field]
                                  : undefined
                          ).map(f => {
                              const prefix = [field as string | number]

                              return {
                                  ...f,
                                  path: prefix.concat(f.path),
                                  uiPath:
                                      f.uiPath === undefined
                                          ? undefined
                                          : prefix.concat(f.uiPath)
                              }
                          })
                        : []
            )
        }

        if (uiField === "https://widgets.super.ai/virtually-structured@0.0.4") {
            return collectStructured((data as any) ?? [], [])
        }

        if (schema.schema.type === "array") {
            if (
                isJSONSchema7(schema.schema.items) &&
                schema.schema.items.$ref === UniversalSchemaType.CHOICE
            ) {
                return []
            }

            return [{ type: "object", path: [] }]
        }

        return []
    }

    const items = collectImpl(rootSchema, rootData)
    const shouldSliceCommonPrefix = items.every(
        item => item.path.length > 1 && item.path[0] === "label"
    )

    return items.map(item => ({
        type: item.type,
        path: item.path,
        label: (item.uiPath ?? item.path)
            .slice(shouldSliceCommonPrefix ? 1 : 0)
            .map(pathElement =>
                typeof pathElement === "number"
                    ? String(pathElement)
                    : upperFirst(lowerCase(pathElement))
            )
            .join(" > ")
    }))
}

export function getSerializedPath(field: FilterableJsonField): string {
    return field.path.join(".")
}
